import { browserLogger } from "@@/settings/browser-logger";
import { ButtonWhite } from "@@/shared/buttons_v2/button-gray";
import { EmptyState } from "@@/shared/components/empty-state";
import { Row } from "@@/shared/components/row";
import { VirtualizedList } from "@@/shared/components/virtualized-list";
import { Conditional } from "@@/shared/conditional";
import { HorizontalDivider, VerticalDivider } from "@@/shared/dividers";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { Icon } from "@@/shared/icons/icon";
import { CheckBoxWithText, TextInput } from "@@/shared/text/text-input";
import { useTranslate } from "@@/translations/use-translate";
import { useTheme } from "@emotion/react";
import {
    faSortAlphaAsc,
    faSortAlphaDesc,
} from "@fortawesome/pro-regular-svg-icons";
import type {
    Resource,
    ResourceGlobal,
    ResourceId,
    Translatable,
} from "@towni/common";
import {
    isInventoryResource,
    isResourceAccommodation,
    isResourceGlobal,
    isResourceGroup,
    isResourceTableGroup,
    remSize,
    sortAlphabeticallyAndNumerically,
    translation,
} from "@towni/common";
import * as React from "react";
import { useCallback, useState } from "react";
import { ResourcePickerRow } from "./resource-picker-row";

type ResourcePickerListProps = {
    resources: Resource[];
    onToggle: (resourceId: ResourceId) => void;
    selected: Set<ResourceId>;
    mode: "SINGLE" | "MULTI";
    emptyText?: Translatable;
};

const ResourcePickerList = (props: ResourcePickerListProps) => {
    const theme = useTheme();
    const translate = useTranslate();
    const [, startTransition] = React.useTransition();
    const [textFilter, setTextFilter] = useState("");
    const [selectedFilter, setSelectedFilter] = useState(false);
    const [groupsFirst, setGroupsFirst] = useState(true);
    const [sort, setSort] = useState<"AlphaAsc" | "AlphaDesc">("AlphaAsc");
    const { resources, selected, mode } = props;

    const filteredResources = React.useMemo(() => {
        const filtered: ResourceGlobal[] = resources
            .filter(isResourceGlobal)
            .filter(resource => {
                // Filter by text value
                const titleFilterMatch =
                    !textFilter ||
                    translate(resource.meta.title)
                        .toLocaleLowerCase()
                        .includes(textFilter.toLocaleLowerCase());

                const accTypeMatch =
                    !textFilter ||
                    (isResourceAccommodation(resource) &&
                        translate(resource.accommodationType.title)
                            ?.toLocaleLowerCase()
                            .includes(textFilter.toLocaleLowerCase()));

                const textFilterMatch = titleFilterMatch || accTypeMatch;

                // Filter if selected
                const selectedFilterMatch =
                    !selectedFilter || selected.has(resource._id);

                // TODO! filter by provider names as well
                return textFilterMatch && selectedFilterMatch;
            });

        const sorted = (resources: ResourceGlobal[]) => {
            const alphabeticallySorted = resources.sort(
                sortAlphabeticallyAndNumerically(item =>
                    translate(item.meta.title),
                ),
            );
            if (sort === "AlphaDesc") {
                return alphabeticallySorted.reverse();
            }
            return alphabeticallySorted;
        };

        if (groupsFirst) {
            const groups = filtered.filter(isResourceGroup);
            const others = filtered.filter(
                r => isInventoryResource(r) || isResourceTableGroup(r),
            );
            return [...sorted(groups), ...sorted(others)];
        }

        return sorted(filtered);
    }, [
        resources,
        groupsFirst,
        textFilter,
        translate,
        selectedFilter,
        selected,
        sort,
    ]);

    const onToggleRef = React.useRef(props.onToggle);
    onToggleRef.current = props.onToggle;
    const renderRow = useCallback(
        (index: number, resource: ResourceGlobal) => {
            const isFirst = index === 0;
            const isLast = index === filteredResources.length - 1;

            return (
                <ResourcePickerRow
                    key={resource._id}
                    resource={resource}
                    toggle={onToggleRef.current}
                    selected={selected.has(resource._id)}
                    padding={{
                        top: isFirst ? 20 : 12,
                        bottom: isLast ? 40 : 0,
                    }}
                />
            );
        },
        [filteredResources.length, selected],
    );

    const allChecked =
        filteredResources.length > 0 &&
        filteredResources.every(r => selected.has(r._id));

    const emptyText =
        props.emptyText ||
        (mode === "MULTI"
            ? translation({
                  sv: "Inga resurser matchar filtreringen",
                  en: "No resources match the filter",
              })
            : translation({
                  sv: "Ingen resurs matchar filtreringen",
                  en: "No resource matches the filter",
              }));
    const EmptyPlaceholder = () => (
        <Row className="size-full p-5">
            <EmptyState className="flex-1" text={emptyText} />
        </Row>
    );

    return (
        <FlexColumn
            fillParentWidth
            crossAxis="flex-start"
            background={{ color: theme.colors.default.background }}>
            <FlexColumn
                fillParentWidth
                background={{ color: theme.colors.primary.light }}
                crossAxis="flex-start"
                padding={{ leftRight: 20, top: 0 }}>
                <VerticalDivider S />
                <FlexRow fillParentWidth mainAxis="space-between">
                    <TextInput
                        type="text"
                        grow={1}
                        padding={{ leftRight: 15, topBottom: 12 }}
                        size={remSize(0.875)}
                        placeholder={translation({
                            sv: "Filtrera",
                            en: "Filter",
                        })}
                        initialValue={textFilter}
                        onChange={value => {
                            startTransition(() => setTextFilter(value));
                        }}
                        onValidationFailed={browserLogger.log}
                    />
                    <HorizontalDivider S />
                    <CheckBoxWithText
                        checked={selectedFilter}
                        text={translation({
                            sv: "Endast valda",
                            en: "Only selected",
                        })}
                        onChange={setSelectedFilter}
                    />
                </FlexRow>
                <VerticalDivider XS />
                <FlexRow
                    fillParentWidth
                    mainAxis="flex-start"
                    crossAxis="stretch">
                    <FlexRow
                        fillParentWidth
                        mainAxis="flex-start"
                        crossAxis="stretch">
                        <ButtonWhite
                            padding={{
                                all: 4,
                            }}
                            onClick={() => {
                                setSort(current =>
                                    current === "AlphaAsc"
                                        ? "AlphaDesc"
                                        : "AlphaAsc",
                                );
                            }}>
                            <Icon
                                icon={
                                    sort === "AlphaAsc"
                                        ? faSortAlphaAsc
                                        : faSortAlphaDesc
                                }
                                fixedWidth
                            />
                        </ButtonWhite>
                        <HorizontalDivider M />
                        <CheckBoxWithText
                            disabled={filteredResources.length === 0}
                            checked={groupsFirst}
                            text={translation({
                                sv: `Grupper först`,
                                en: `Groups first`,
                            })}
                            onChange={() => {
                                setGroupsFirst(current => !current);
                            }}
                        />
                        <Conditional
                            when={mode === "MULTI" && resources.length > 0}>
                            <HorizontalDivider M />
                            <FlexRow
                                mainAxis="flex-start"
                                css={{
                                    minWidth: "165px",
                                }}>
                                <CheckBoxWithText
                                    disabled={filteredResources.length === 0}
                                    checked={allChecked}
                                    text={
                                        allChecked
                                            ? translation({
                                                  sv: `Avmarkera alla (${filteredResources.length})`,
                                                  en: `Deselect all (${filteredResources.length})`,
                                              })
                                            : translation({
                                                  sv: `Välj alla (${filteredResources.length})`,
                                                  en: `Select all (${filteredResources.length})`,
                                              })
                                    }
                                    onChange={() => {
                                        if (allChecked) {
                                            filteredResources.forEach(
                                                resource => {
                                                    onToggleRef.current(
                                                        resource._id,
                                                    );
                                                },
                                            );
                                        } else {
                                            filteredResources.forEach(
                                                resource => {
                                                    if (
                                                        !selected.has(
                                                            resource._id,
                                                        )
                                                    ) {
                                                        onToggleRef.current(
                                                            resource._id,
                                                        );
                                                    }
                                                },
                                            );
                                        }
                                    }}
                                />
                            </FlexRow>
                        </Conditional>
                    </FlexRow>
                </FlexRow>
                <VerticalDivider S />
            </FlexColumn>
            <Row crossAxis="stretch" className="h-[60vh] w-full">
                <VirtualizedList
                    className="flex-1"
                    key={`${sort}-${groupsFirst}-${textFilter}-${selectedFilter}`}
                    data={filteredResources}
                    itemContent={renderRow}
                    components={{
                        EmptyPlaceholder,
                    }}
                />
            </Row>
        </FlexColumn>
    );
};
export { ResourcePickerList };
